<script context="module">
  import Layout from '@/Layouts/Main.svelte';
  export const layout = Layout;
</script>

<script>
import { inertia } from '@inertiajs/inertia-svelte';
import { onDestroy, onMount } from 'svelte';
import mapboxgl from 'mapbox-gl';
import { drawMarkers, fitBounds } from '@/utils/map.js';
import { filterData, disableOptions } from '@/utils/data.js';
import CityInfo from '@/Partials/CityInfo.svelte';
import CityList from '@/Partials/CityList.svelte';
import Principles from '@/Partials/Principles.svelte';
import Filter from '@/Partials/Filter.svelte';
import { trans } from '@/utils';


export let mapbox_token;
export let cities = [];
export let options = {regions: [], principles: [], sectors: [], types: [], statuses: [], sizes: [],
  stakeholders: [], policies: [], ai_types: [], local_govt: [], organizations: []
};
export let url = '';
export let sel_principles = [];
export let sel_city;
export let settings = {};

let filter = {};

let activeCities = [];

let numFilters = 0;
let activePrinciples = [];

let markers = [];
let activeMarker = null;

let activeView = null;

onMount(() => {
  if (sel_principles.length) {
    filter.principles = sel_principles;
  }
});

$: totalCities = cities.length;
$: totalProjects = cities.reduce((prev, cur) => { return prev + cur.projects.length; }, 0);


$: {
  activeCities = filterData(cities, filter, options);
  if (activeView === 'map') {
    redrawMarkers();
  } else {
    removeMarkers();
  }
}

$: numCities = activeCities.length;
$: numProjects = activeCities.reduce((prev, cur) => { return prev + cur.projects.length; }, 0);

$: principleCounts = options.principles.reduce((pre, principle) => {
  activeCities.forEach(city => {
    const num = city.projects.filter(project => project.principles.some(pri => pri === principle.id)).length;
    pre[principle.id] = num + (pre[principle.id] || 0);
  });
  return pre;
}, {});

$: {
  if (filter.principles && filter.principles.length) activePrinciples = filter.principles;
  else activePrinciples = options.principles.map(p => p.id);
}

$: {
  numFilters = 0;
  for (const k in filter) {
    if (k === 'principles') {
      if (filter.principles.length < options.principles.length) numFilters = numFilters + filter.principles.length;
    } else {
      if (Array.isArray(filter[k])) numFilters = numFilters + filter[k].length;
      else { // scalars: ccdr, organization
        if (filter[k]) numFilters = numFilters + 1;
      }
    }
  }
}

$: disabledOptions = disableOptions(filter, cities, options);

let container;
let map;
let lng = -8;
let lat = 29;
let zoom = 1.8;

let info_closed = true;
let selected_city = null;

let filter_closed = true;
let filter_toggle = false;

mapboxgl.accessToken = mapbox_token;

function loadMap() {
  map = new mapboxgl.Map({
      container,
      style: 'mapbox://styles/davidsolav/cl4mfv7m7000014plnxmapt21',
      center: [lng, lat],
      zoom,
      maxZoom: 9,
      attributionControl: false,
      // projection: 'naturalEarth'
  });
  const nav = new mapboxgl.NavigationControl();
  map.on('load', () => {
    activeView = 'map'; // force redraw
  });

}

onDestroy(() => {
  if (map) map.remove();
});

function removeMarkers() {
  markers.forEach(m => {
      m.getElement().removeEventListener('click', showCityInfo);
      m.remove();
  });
}

function redrawMarkers() {
  removeMarkers();
  markers = drawMarkers(map, activeCities, showCityInfo);
  if (sel_city) {
    markers.forEach(m => {
      const el = m.getElement();
      if (el.dataset.city_id == sel_city) {
        el.click();
      }
    });
    sel_city = null;
  }
}

async function showCityInfo(e) {
  const el = e.currentTarget;
  if (activeMarker) activeMarker.classList.remove('active');
  activeMarker = el;
  el.classList.add('active');
  const id = e.currentTarget.dataset.city_id;

  const data = await fetchCityInfo(id);
  selected_city = data;
  info_closed = false;

  const params = {center: [selected_city.lng, selected_city.lat], zoom: 9, speed: 1.5};
  if (!window.matchMedia('(max-width: 991px)').matches) {
    // bug: https://github.com/mapbox/mapbox-gl-js/issues/11831
    // params.padding = {top: 100, bottom: 100, left: 100, right: 740};
    params.center[0] = params.center[0] + 0.4;
  }
  map.flyTo(params);
}

async function selectCity(id) {
  const data = await fetchCityInfo(id);
  selected_city = data;
  info_closed = false;
}

async function fetchCityInfo(id) {
  const city = activeCities.find(el => el.id == id);
  const project_ids = city.projects.map(el => el.id).join(',');
  return fetch(`${url}/city/${id}?`+ new URLSearchParams({p: project_ids})).then((response) => response.json());
}


const zoomIn = () => { if (map) map.zoomTo(0.5+map.getZoom()) };
const zoomOut = () => { if (map) map.zoomTo(-0.5+map.getZoom()) };

const switchView = (mode) => {
  if (mode === 'map' && activeView !== 'map') {
    activeView = 'map';
  } else if (mode === 'list' && activeView !== 'list') {
    activeView = 'list';
  }
}

const applyFilter = (e) => { filter = { ...filter, ...e.detail }; };
const resetFilter = () => { filter = {}; filter_closed = true; filter_toggle = !filter_toggle;};

const closeInfo = () => {
  info_closed = true;
  selected_city = null;
  if (activeView === 'map') fitBounds(map, markers);
}

const toggleHelp = (e) => {
  e.currentTarget.classList.toggle('active');
  document.querySelectorAll('.helpbox').forEach(el => { el.classList.toggle('active'); });
}

</script>
<svelte:head>
  <link rel="stylesheet" href="https://api.mapbox.com/mapbox-gl-js/v2.7.0/mapbox-gl.css" on:load={loadMap} />
  <link rel="stylesheet" href="{url}/css/tooltips.css" />
  <script src="https://code.highcharts.com/highcharts.js"></script>
</svelte:head>



<div class="position-relative">
  <div class="container position-absolute top-0 start-50 translate-middle-x z1">

      <div class="d-flex justify-content-between">

        <div>
          <div class="position-relative z1 pt-lg-5 pt-4">



            <div class="pb-4">
              <div class="">
                <div>
                  <h1 class="m-0">{trans('main_title')}</h1>
                </div>
              </div>
            </div>

            <div class="d-flex align-items-center gap-3 lh-1">
              <div class="pointer-event-all">
                <button type="button" on:click="{() => {filter_closed = !filter_closed}}" class="btn btn-sm btn-light border-0 text-decoration-none">
                  {#if filter_closed}
                  {trans('btn_show_filters')} {#if numFilters}({numFilters}){/if} <i class="fa-light fa-arrow-up-right"></i>
                  {:else}
                  {trans('btn_hide_filters')} <i class="fa-light fa-arrow-down-right"></i>
                  {/if}
                </button>
              </div>

              {#if numFilters}
              <div class="">
                <button type="button" class="btn btn-sm btn-link p-0 border-0 text-decoration-none text-white" on:click={resetFilter}>
                  <i class="fa-light fa-arrow-rotate-right me-1"></i> {trans('btn_reset_filters')}
                </button>
              </div>
              {/if}
            </div>


            <div class="helpbox help" id="help2">
              {@html settings.help_text_2}
            </div>
            

          </div>
        </div>

        <div>
          <div class="position-relative z1 pt-4">

            <div class="helpbox help" id="help1">
              {@html settings.help_text_1}
            </div>

            <div>
              <div class="d-lg-flex justify-content-between pb-2 mb-3 border-bottom share_block d-none">
                <a href="http://www.twitter.com/share?url={encodeURIComponent(url)}" target="_blank">
                  <i class="fa-brands fa-twitter"></i>
                </a>
                <a href="https://www.facebook.com/sharer/sharer.php?u={encodeURIComponent(url)}" target="_blank">
                  <i class="fa-brands fa-facebook"></i>
                </a>
                <a href="https://www.linkedin.com/shareArticle?mini=true&url={encodeURIComponent(url)}&title={encodeURIComponent("GOUAI's Atlas of Urban AI")}" target="_blank">
                  <i class="fa-brands fa-linkedin"></i>
                </a>
                <a href="mailto:?subject={encodeURIComponent("GOUAI's Atlas of Urban AI")}&body={encodeURIComponent(url)}">
                  <i class="fa-regular fa-envelope"></i>
                </a>
                <div class="border-end">
                </div>
                <div class="position-relative">
                  <a href="/contribute/">
                    {trans('link_contribute')}
                  </a>
                  <div class="helpbox help" id="help4">
                    {@html settings.help_text_4}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <div class="fs-4 lh-sm num_projects d-none d-lg-block pointer-event-all">
                    <div>{#if numFilters}{numProjects}/{totalProjects}{:else}{numProjects}{/if} {trans('n_initiatives')}</div>
                    <div>{#if numFilters}{numCities}/{totalCities}{:else}{numCities}{/if} {trans('n_cities')}</div>
                  </div>
                </div>

                <!-- toggle menu -->
                <div class="menu_wrap">

                  <div class="btn_menu_content">
                    <button class="btn_menu collapsed" data-bs-toggle="collapse" href="#site-navigation" aria-expanded="false" aria-controls="site-navigation">
                      <!--<i class="fa-light fa-bars"></i>-->
                      <span></span>
                      <span></span>
                      <span></span>
                    </button>
                  </div>

                  <nav id="site-navigation" class="main-navigation collapse">
                    <div class="nav_menu text-end">
                      <ul class="list-unstyled p-0 m-0">
                        <li><a href="/">GOUAI</a></li>
                        <li><a href="/#welcome">About us</a></li>
                        <li><a href="/#principles">GOUAI's Principles</a></li>
                        <li><a href="/#atlas">Atlas of Urban AI</a></li>
                        <li><a href="/contribute/">{trans('link_contribute')}</a></li>
                        <li><a href="/#documentary">Documentary</a></li>
                        <li><a href="/#resources">Resources</a></li>
                        <li><a href="/#news">News</a></li>
                      </ul>
                    </div>
                  </nav>

                </div>
                <!-- toggle menu -->
              </div>
            </div>

            <div class="fl_map_control d-none d-lg-block">
              <div class="py-lg-5 py-4">
                <div>
                  <button type="button" class="btn_rounded_transparent d-block" class:active={activeView === 'list'} on:click={() => { switchView('list', true)} }>{trans('btn_list')}</button>
                  <button type="button" class="btn_rounded_transparent d-block" class:active={activeView === 'map'} on:click={() => { switchView('map', true)} }>{trans('btn_map')}</button>
                </div>
                <div class="pt-2">
                  <button type="button" class="btn_rounded_transparent sm d-block" on:click={zoomIn}><i class="fa-light fa-plus"></i></button>
                  <button type="button" class="btn_rounded_transparent sm d-block" on:click={zoomOut}><i class="fa-light fa-minus"></i></button>
                </div>
                <div class="pt-2">
                  <button type="button" class="btn_rounded_transparent sm help_button" title="Help" on:click={(e) => { toggleHelp(e) }}><i class="fa-light fa-question"></i></button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>



    </div>
</div>
<!-- container -->


<div id="map" bind:this={container}></div>




{#if activeView === 'list'}
<CityList cities={activeCities} principles={options.principles} active={activePrinciples} by="name" on:selected={(e) => selectCity(e.detail)} />
{/if}


<CityInfo principles={options.principles} closed={info_closed} on:close={closeInfo} city={selected_city} />

<Filter closed={filter_closed} on:close={() => { filter_closed = true}} clear_toggle={filter_toggle} data={options} {disabledOptions} on:change={applyFilter} on:reset={resetFilter} />


  <div class="d-none d-lg-block">
    <Principles principles={options.principles} help={settings.help_text_3} counters={principleCounts} active={activePrinciples} total={numProjects} on:change={(e) => filter.principles = e.detail} />
  </div>


<div class="mobile_bottom_bar d-block d-lg-none">

  <div class="container">
    <div class="d-flex justify-content-between flex-wrap small">
      <div class="d-flex align-items-center flex-fill">
        <div>
          <button type="button" class="btn_rounded_transparent d-lg-block me-2" class:active={activeView === 'list'} on:click={() => { switchView('list', true)} }>{trans('btn_list')}</button>
          <button type="button" class="btn_rounded_transparent d-lg-block" class:active={activeView === 'map'} on:click={() => { switchView('map', true)} }>{trans('btn_map')}</button>
        </div>
      </div>
      <div class="d-flex align-items-center justify-content-center flex-fill">
        <div>
          <div>{#if numFilters}{numProjects}/{totalProjects}{:else}{numProjects}{/if} {trans('n_initiatives')}</div>
          <div>{#if numFilters}{numCities}/{totalCities}{:else}{numCities}{/if} {trans('n_cities')}</div>
        </div>
      </div>
      <div class="d-flex align-items-center justify-content-end flex-fill">
        <a class="text-white btn_show_principles collapsed text-decoration-none d-flex align-items-center" data-bs-toggle="collapse" href="#collapseExample" role="button" aria-expanded="false" aria-controls="collapseExample">
          <div class="me-2">
            <i class="fa-light fa-arrow-up"></i>
          </div>
          <div class="lh-1">
            <span class="show_t">{trans('show')}</span>
            <span class="hide_t">{trans('hide')}</span>
            {trans('principles')}
          </div>
        </a>
      </div>
    </div>
  </div>





  <div class="collapse" id="collapseExample">
    <Principles principles={options.principles} counters={principleCounts} active={activePrinciples} total={numProjects} on:change={(e) => filter.principles = e.detail} />
  </div>
</div>
